// Enrollment validation and scheduling conflict detection
//
// Two modes of conflict checking:
//   - Block-based (wouldConflictByBlock): enrollment gatekeeper. Prevents a student
//     from being double-booked within a block. Hard gate on enrollment.
//   - Time-based (wouldConflictByTime): scheduling visibility. Shows whether two
//     activities overlap in actual time. Informational, never blocks enrollment.
//
// All functions are pure — they take activity/enrollment objects, not IDs.
// The calling code is responsible for loading data and passing it in.
//
// Block-based conflict detection operates on "effective schedules" — the intersection
// of an enrollment's scheduling constraints with the parent activity's schedule.
// Use getEffectiveSchedule (from scheduleUtils) to compute these before calling
// wouldConflictByBlock or couldMeetOnSameDay.

import { getEffectiveSchedule } from '@/lib/scheduleUtils'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Check whether two arrays of day-of-week values share any common day.
 * Days use EXTRACT(DOW) convention: 0=Sun, 1=Mon, ..., 6=Sat.
 */
function daysOverlap(daysA, daysB) {
  if (!daysA || !daysB) return false
  const setB = new Set(daysB)
  return daysA.some((d) => setB.has(d))
}

/**
 * Parse a time string ("HH:MM" or "HH:MM:SS") into total minutes since midnight.
 * Returns null if the input is falsy or unparseable.
 */
function timeToMinutes(timeStr) {
  if (!timeStr) return null
  const parts = timeStr.split(':')
  if (parts.length < 2) return null
  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1], 10)
  if (isNaN(hours) || isNaN(minutes)) return null
  return hours * 60 + minutes
}

/**
 * Calculate overlap or gap between two time ranges in minutes.
 * Returns { overlaps, overlapMinutes, gapMinutes }.
 *   - overlaps: true if the ranges share any time
 *   - overlapMinutes: how many minutes they overlap (0 if no overlap)
 *   - gapMinutes: how many minutes between them (0 if they overlap)
 */
function computeTimeOverlap(startA, endA, startB, endB) {
  const overlapStart = Math.max(startA, startB)
  const overlapEnd = Math.min(endA, endB)

  if (overlapStart < overlapEnd) {
    return {
      overlaps: true,
      overlapMinutes: overlapEnd - overlapStart,
      gapMinutes: 0,
    }
  }

  // No overlap — calculate the gap
  const gap = overlapStart - overlapEnd
  return {
    overlaps: false,
    overlapMinutes: 0,
    gapMinutes: gap,
  }
}

/**
 * Determine whether two activities could meet on any shared day,
 * considering days_of_week, rotation_day_type, and recurrence_interval.
 *
 * NOTE: Recurrence awareness handles the equal-interval alternating-week case.
 * Unequal intervals or missing anchor dates fall through conservatively (false positive).
 * False positives (flagging a non-conflict) are acceptable; false negatives are not.
 *
 * Returns { couldMeetSameDay, reason }
 *   - couldMeetSameDay: true if there's any day both activities would meet
 *   - reason: human-readable explanation of why/why not
 */
function couldMeetOnSameDay(activityA, activityB) {
  const aHasDays = activityA.days_of_week != null && activityA.days_of_week.length > 0
  const bHasDays = activityB.days_of_week != null && activityB.days_of_week.length > 0
  const aHasRotation = activityA.rotation_day_type != null
  const bHasRotation = activityB.rotation_day_type != null

  // Case 1: Both use days_of_week — conflict if any shared day
  if (aHasDays && bHasDays) {
    const shared = daysOverlap(activityA.days_of_week, activityB.days_of_week)
    if (!shared) {
      return { couldMeetSameDay: false, reason: 'Activities meet on different weekdays' }
    }

    // Both share at least one weekday — check whether recurrence intervals guarantee
    // they never land on the same week. Only applied when both have interval > 1 and
    // anchor dates. If either is missing, fall through conservatively.
    const aInterval = activityA.recurrence_interval ?? 1
    const bInterval = activityB.recurrence_interval ?? 1
    const aAnchor = activityA.recurrence_anchor_date
    const bAnchor = activityB.recurrence_anchor_date

    if (aInterval > 1 && bInterval > 1 && aAnchor && bAnchor && aInterval === bInterval) {
      const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000
      const epochMs = new Date('2024-01-01').getTime()
      const aWeekOffset = Math.round((new Date(aAnchor).getTime() - epochMs) / MS_PER_WEEK)
      const bWeekOffset = Math.round((new Date(bAnchor).getTime() - epochMs) / MS_PER_WEEK)
      const aPhase = ((aWeekOffset % aInterval) + aInterval) % aInterval
      const bPhase = ((bWeekOffset % bInterval) + bInterval) % bInterval

      if (aPhase !== bPhase) {
        return {
          couldMeetSameDay: false,
          reason: `Activities meet on the same weekday(s) but on alternating weeks (interval ${aInterval}, different phases)`,
        }
      }
    }

    return { couldMeetSameDay: true, reason: 'Both activities meet on overlapping weekdays' }
  }

  // Case 2: Both use rotation_day_type — conflict only if same rotation day
  if (aHasRotation && bHasRotation) {
    const same = activityA.rotation_day_type === activityB.rotation_day_type
    return {
      couldMeetSameDay: same,
      reason: same
        ? `Both activities meet on ${activityA.rotation_day_type} days`
        : `Activities meet on different rotation days (${activityA.rotation_day_type} vs ${activityB.rotation_day_type})`,
    }
  }

  // Case 3: One uses days_of_week, the other uses rotation_day_type
  // A days-of-week activity meets on both rotation days, so it will always
  // collide with a rotation-day activity on that rotation day's occurrences.
  if ((aHasDays && bHasRotation) || (aHasRotation && bHasDays)) {
    return {
      couldMeetSameDay: true,
      reason:
        'One activity uses fixed weekdays and the other uses rotation days — ' +
        'the fixed-day activity meets on both rotation days, causing overlap',
    }
  }

  // Case 4: Neither has scheduling info — treat as conflict (data entry incomplete)
  return {
    couldMeetSameDay: true,
    reason: 'Neither activity has day/rotation scheduling info — assuming conflict',
  }
}

// ---------------------------------------------------------------------------
// Block-based conflict detection (enrollment gatekeeper)
// ---------------------------------------------------------------------------

/**
 * Check whether two effective schedules conflict within the block system.
 * This is the enrollment gatekeeper — if this returns conflicts: true,
 * the enrollment should be rejected.
 *
 * Accepts effective-schedule objects (output of getEffectiveSchedule), not raw
 * activity objects. This allows two enrollments in the same activity at the same
 * block to be non-conflicting with other activities when their effective days differ.
 *
 * @param {{ block, days_of_week, rotation_day_type, recurrence_interval, recurrence_anchor_date }} effectiveA
 * @param {{ block, days_of_week, rotation_day_type, recurrence_interval, recurrence_anchor_date }} effectiveB
 * @returns {{ conflicts: boolean, reason: string|null }}
 */
export function wouldConflictByBlock(effectiveA, effectiveB) {
  const blocksA = Array.isArray(effectiveA.block) ? effectiveA.block : (effectiveA.block != null ? [effectiveA.block] : [])
  const blocksB = Array.isArray(effectiveB.block) ? effectiveB.block : (effectiveB.block != null ? [effectiveB.block] : [])

  if (blocksA.length === 0 || blocksB.length === 0) {
    return { conflicts: false, reason: null }
  }

  const setB = new Set(blocksB)
  const sharedBlocks = blocksA.filter(b => setB.has(b))
  if (sharedBlocks.length === 0) {
    return { conflicts: false, reason: null }
  }

  // Shared blocks found — check day/rotation overlap
  const { couldMeetSameDay, reason } = couldMeetOnSameDay(effectiveA, effectiveB)
  const blockNames = sharedBlocks.map(b => `Block ${b}`).join(', ')

  return {
    conflicts: couldMeetSameDay,
    reason: couldMeetSameDay
      ? `Both activities share ${blockNames}: ${reason}`
      : null,
  }
}

// ---------------------------------------------------------------------------
// Time-based conflict detection (scheduling visibility — informational)
// ---------------------------------------------------------------------------

/**
 * Check whether two activities overlap in actual time on any shared day.
 * This is informational — for scheduling visibility, not enrollment gatekeeping.
 *
 * Activities with no times (is_not_scheduled, or times not yet entered) are
 * treated as non-conflicting since there's nothing to compare.
 *
 * @param {Object} activityA - Activity object with default_start_time, default_end_time, days_of_week, rotation_day_type
 * @param {Object} activityB - Activity object with default_start_time, default_end_time, days_of_week, rotation_day_type
 * @returns {{ overlaps: boolean, overlapMinutes: number|null, gapMinutes: number|null, details: string }}
 */
export function wouldConflictByTime(activityA, activityB) {
  const noConflict = { overlaps: false, overlapMinutes: null, gapMinutes: null, details: '' }

  // If either activity has no times, we can't compare
  const startA = timeToMinutes(activityA.default_start_time)
  const endA = timeToMinutes(activityA.default_end_time)
  const startB = timeToMinutes(activityB.default_start_time)
  const endB = timeToMinutes(activityB.default_end_time)

  if (startA == null || endA == null || startB == null || endB == null) {
    return {
      ...noConflict,
      details: 'One or both activities have no scheduled times',
    }
  }

  // Check whether they could meet on the same day
  const { couldMeetSameDay, reason: dayReason } = couldMeetOnSameDay(activityA, activityB)

  if (!couldMeetSameDay) {
    return {
      ...noConflict,
      details: `No time conflict — ${dayReason}`,
    }
  }

  // They share days — check time overlap
  const { overlaps, overlapMinutes, gapMinutes } = computeTimeOverlap(
    startA,
    endA,
    startB,
    endB
  )

  if (overlaps) {
    return {
      overlaps: true,
      overlapMinutes,
      gapMinutes: 0,
      details: `Activities overlap by ${overlapMinutes} minutes on shared days`,
    }
  }

  return {
    overlaps: false,
    overlapMinutes: 0,
    gapMinutes,
    details: `Activities have a ${gapMinutes}-minute gap on shared days`,
  }
}

// ---------------------------------------------------------------------------
// Enrollment validation (public — what the enrollment flow calls)
// ---------------------------------------------------------------------------

/**
 * Validate whether a student can be enrolled in a new activity, given their
 * existing enrollments. Uses block-based conflict detection with enrollment-effective
 * schedules — two enrollments in the same block only conflict if their effective
 * days_of_week actually overlap.
 *
 * Each enrollment in existingEnrollments must have its activity joined in,
 * i.e. enrollment.activity should be the full activity object.
 * (This matches the shape returned by getOrgEnrollments.)
 *
 * @param {Object} newActivity - The activity the student would be enrolled in
 * @param {{ days_of_week?, rotation_day_type?, recurrence_interval?, recurrence_anchor_date? }|null} newEnrollmentSchedule
 *   Proposed per-enrollment scheduling constraints for the new enrollment.
 *   Pass null (or omit) for the default "follow the activity" behavior.
 * @param {Array} existingEnrollments - Student's current enrollments, each with .activity
 * @returns {{ valid: boolean, conflicts: Array<{ enrollment: Object, activity: Object, reason: string }> }}
 */
export function validateEnrollment(newActivity, newEnrollmentSchedule, existingEnrollments) {
  const newBlocks = Array.isArray(newActivity.block) ? newActivity.block : (newActivity.block != null ? [newActivity.block] : [])
  // Unscheduled activities (no block) can't conflict by block
  if (newBlocks.length === 0) {
    return { valid: true, conflicts: [] }
  }

  const newEffective = getEffectiveSchedule(newEnrollmentSchedule ?? {}, newActivity)
  const newBlockSet = new Set(newBlocks)
  const conflicts = []

  for (const enrollment of existingEnrollments) {
    const existingActivity = enrollment.activity
    if (!existingActivity) continue

    // Only check enrollments where block arrays intersect
    const existingBlocks = Array.isArray(existingActivity.block)
      ? existingActivity.block
      : (existingActivity.block != null ? [existingActivity.block] : [])
    if (!existingBlocks.some(b => newBlockSet.has(b))) continue

    const existingEffective = getEffectiveSchedule(enrollment, existingActivity)
    const result = wouldConflictByBlock(newEffective, existingEffective)

    if (result.conflicts) {
      conflicts.push({
        enrollment,
        activity: existingActivity,
        reason: result.reason,
      })
    }
  }

  return {
    valid: conflicts.length === 0,
    conflicts,
  }
}

// ---------------------------------------------------------------------------
// Schedule analysis utilities
// ---------------------------------------------------------------------------

/**
 * Find which blocks are open (no enrollments) for a single student.
 *
 * @param {Array} studentEnrollments - Student's active enrollments, each with .activity
 * @param {Object} orgSettings - Organization settings with block_count
 * @returns {Array<{ block: number, label: string, isOpen: boolean, activities: Array }>}
 */
export function findAvailableBlocks(studentEnrollments, orgSettings) {
  const blockCount = orgSettings?.block_count
  if (!blockCount || blockCount < 1) return []

  // Build a map of block -> activities the student is enrolled in
  const blockMap = new Map()
  for (let i = 0; i < blockCount; i++) {
    blockMap.set(i, [])
  }

  for (const enrollment of studentEnrollments) {
    const activity = enrollment.activity
    if (!activity) continue
    const actBlocks = Array.isArray(activity.block) ? activity.block : (activity.block != null ? [activity.block] : [])
    for (const b of actBlocks) {
      if (blockMap.has(b)) blockMap.get(b).push(activity)
    }
  }

  return Array.from(blockMap.entries()).map(([block, activities]) => ({
    block,
    label: `Block ${block}`,
    isOpen: activities.length === 0,
    activities,
  }))
}

/**
 * Find all time-based conflicts between a given activity and a list of other
 * activities. Returns only pairs that actually overlap in time.
 *
 * Useful for scheduling visibility: "if I place this activity here, what
 * does it overlap with?"
 *
 * @param {Object} activity - The activity to check
 * @param {Array} otherActivities - Activities to compare against
 * @returns {Array<{ activity: Object, overlapMinutes: number, details: string }>}
 */
export function findTimeConflicts(activity, otherActivities) {
  const conflicts = []

  for (const other of otherActivities) {
    // Don't compare an activity to itself
    if (other.id === activity.id) continue

    const result = wouldConflictByTime(activity, other)

    if (result.overlaps) {
      conflicts.push({
        activity: other,
        overlapMinutes: result.overlapMinutes,
        details: result.details,
      })
    }
  }

  return conflicts
}
